import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaSpinner, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import OtpBoxInput from './OtpBoxInput';
import { verifyLoginOtp, resendLoginOtp, mapVerifyOtpError, mapResendOtpError } from '../../services/loginOtpApi';
import { useAuth } from '../../auth/AuthContext';

const RESEND_SECONDS = 30;
const WHATSAPP_OTP_URL = process.env.REACT_APP_WHATSAPP_OTP_URL || 'https://wa.me/919876543210?text=Send%20me%20my%20SparePe%20OTP';

const EMPTY_OTP = ['', '', '', '', '', ''];

const toOtpArray = (value) =>
  Array.from({ length: 6 }, (_, i) => (String(value[i] || '').replace(/\D/g, '').slice(0, 1)));

const LoginOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { establishCookieSession } = useAuth();

  const [phone, setPhone] = useState(location.state?.phone || '');
  const [otpId, setOtpId] = useState(location.state?.otpId || '');
  const [otp, setOtp] = useState(EMPTY_OTP);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [resendTimer, setResendTimer] = useState(RESEND_SECONDS);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(false);
  const [resendFailedOnce, setResendFailedOnce] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  const timerRef = useRef(null);

  useEffect(() => {
    if (!phone || !otpId) {
      navigate('/login-phone', { replace: true });
    }
  }, [phone, otpId, navigate]);

  useEffect(() => {
    setResendTimer(RESEND_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [otpId]);

  const handleOtpChange = (value) => {
    setOtp(toOtpArray(value));
    setError('');
  };

  const otpString = otp.join('');

  const navigateAfterLogin = useCallback(
    (result) => {
      if (result.isNewUser) {
        navigate('/vehicle-picker', {
          replace: true,
          state: { message: 'Add your car to get started.' },
        });
        return;
      }
      if (result.hasVehicles) {
        navigate('/', { replace: true });
        return;
      }
      navigate('/vehicle-picker', {
        replace: true,
        state: { message: 'Add your car to get started.' },
      });
    },
    [navigate]
  );

  const handleVerify = async (e) => {
    e.preventDefault();
    if (locked || otpString.length !== 6) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await verifyLoginOtp({ otpId, phone, otp: otpString });
      establishCookieSession({
        userId: result.userId,
        displayName: result.displayName,
        isNewUser: result.isNewUser,
      });
      navigateAfterLogin(result);
    } catch (err) {
      const mapped = mapVerifyOtpError(err);
      if (mapped.toast) toast.error(mapped.message);
      setError(mapped.message);
      if (mapped.attemptsLeft !== undefined) setAttemptsLeft(mapped.attemptsLeft);
      if (mapped.locked) setLocked(true);
      if (mapped.clearOtp) setOtp(EMPTY_OTP);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || isResending || locked) return;

    if (!navigator.onLine) {
      toast.error('No internet connection. Check your network.');
      return;
    }

    setIsResending(true);
    setError('');

    try {
      const result = await resendLoginOtp({ otpId, phone });
      if (result.otpId) setOtpId(result.otpId);
      setOtp(EMPTY_OTP);
      setAttemptsLeft(3);
      setLocked(false);
      setResendTimer(RESEND_SECONDS);
      toast.success('OTP sent again');
    } catch (err) {
      const mapped = mapResendOtpError(err);
      setResendFailedOnce(true);
      setShowWhatsApp(true);
      if (mapped.toast) toast.error(mapped.message);
      setError(mapped.message);
      if (mapped.locked) setLocked(true);
    } finally {
      setIsResending(false);
    }
  };

  if (!phone || !otpId) return null;

  const maskTop = `+91 ${phone.slice(0, 5)} XXXXX`;
  const maskSub = `+91 XXXXXX${phone.slice(-4)}`;
  const canVerify = otpString.length === 6 && !isLoading && !locked;
  const boxesDisabled = isLoading || locked;

  return (
    <Page>
      <TopBar>
        <BackButton
          type="button"
          onClick={() => navigate('/login-phone', { state: { from: location.state?.from } })}
          aria-label="Back to phone entry"
        >
          <FaArrowLeft />
        </BackButton>
      </TopBar>

      <Card>
        <PhoneDisplay>{maskTop}</PhoneDisplay>

        <Heading>Enter OTP</Heading>
        <Subheading>Sent to {maskSub}</Subheading>

        <Form onSubmit={handleVerify}>
          <OtpBoxInput
            value={otpString}
            onChange={handleOtpChange}
            disabled={boxesDisabled}
            hasError={Boolean(error)}
            autoFocus
          />

          {error && <InlineError>{error}</InlineError>}

          <SubmitButton type="submit" disabled={!canVerify} $active={canVerify}>
            {isLoading ? (
              <>
                <FaSpinner className="spin" aria-hidden="true" />
                Verifying…
              </>
            ) : (
              'Verify OTP'
            )}
          </SubmitButton>
        </Form>

        <ResendRow>
          {resendTimer > 0 ? (
            <ResendText>Resend OTP in 0:{String(resendTimer).padStart(2, '0')}</ResendText>
          ) : (
            <ResendLink
              type="button"
              onClick={handleResend}
              disabled={isResending || locked || isLoading}
            >
              {isResending ? 'Resending…' : 'Resend OTP'}
            </ResendLink>
          )}
        </ResendRow>

        {showWhatsApp && resendFailedOnce && (
          <WhatsAppLink href={WHATSAPP_OTP_URL} target="_blank" rel="noopener noreferrer">
            Didn&apos;t receive SMS? Get OTP on WhatsApp
          </WhatsAppLink>
        )}
      </Card>
    </Page>
  );
};

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 24px 16px;
  position: relative;
`;

const TopBar = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PhoneDisplay = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
`;

const Heading = styled.h1`
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
`;

const Subheading = styled.p`
  margin: 0 0 28px;
  font-size: 15px;
  color: #64748b;
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InlineError = styled.p`
  margin: -8px 0 0;
  font-size: 13px;
  color: #dc2626;
  text-align: center;
`;

const SubmitButton = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$active ? '#2563eb' : '#cbd5e1')};
  color: ${(p) => (p.$active ? '#fff' : '#94a3b8')};

  &:disabled {
    cursor: not-allowed;
  }

  .spin {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ResendRow = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const ResendText = styled.span`
  font-size: 14px;
  color: #64748b;
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: #2563eb;
  font-size: 14px;
  cursor: pointer;
  padding: 0;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const WhatsAppLink = styled.a`
  margin-top: 16px;
  font-size: 14px;
  color: #16a34a;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default LoginOTP;
